import React, { useCallback, useEffect, useState } from 'react';
import { Gem, Loader2, ScrollText, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { gbApi, gbFmt } from './gbApi';
import { GbCoin } from './GbCoin';

export const LEDGER_LABEL = {
  recharge: 'Diamantes por compra de moedas',
  mission_entry: 'Entrada de fase',
  mission_reward: 'Recompensa de fase',
  mission_coins: 'Moedas ganhas na fase',
  pvp_entry: 'Entrada do duelo PvP',
  pvp_prize: 'Prêmio do duelo PvP',
  pvp_entry_refund: 'Devolução da entrada do PvP',
  coin_purchase: 'Compra de moedas no jogo',
  exchange_out: 'Intercâmbio: diamantes trocados',
  exchange_in: 'Intercâmbio: moedas recebidas',
  withdraw_request: 'Resgate para a conta KAKO',
  withdraw_refund: 'Resgate devolvido ao saldo',
  season_close_payout: 'Saldo devolvido no fim do evento',
  upgrade: 'Melhoria',
  item: 'Item de batalha',
  welcome: 'Bônus de entrada',
  admin_adjust: 'Ajuste do administrador',
  clan_create: 'Fundação de clã',
};

const Chip = ({ on, children, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full px-3 py-1 text-[11px] font-black transition-colors ${on
      ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-[#1b0d02]'
      : 'border border-purple-800/70 text-purple-300/70 hover:text-amber-200'}`}>
    {children}
  </button>
);

const PERIODOS = [{ v: 0, t: 'tudo' }, { v: 7, t: '7 dias' }, { v: 30, t: '30 dias' }];

export const GbStatement = ({ scope = 'all', title = 'EXTRATO DO EVENTO', testid = 'gb-statement' }) => {
  const [dados, setDados] = useState(null);
  const [load, setLoad] = useState(true);
  const [moeda, setMoeda] = useState(scope === 'all' ? 'all' : scope);
  const [fluxo, setFluxo] = useState('all');
  const [tipo, setTipo] = useState('all');
  const [dias, setDias] = useState(0);

  const buscar = useCallback(async () => {
    setLoad(true);
    try {
      const url = scope === 'coins'
        ? `/coins/statement?limit=120&flow=${fluxo}`
        : scope === 'diamonds'
          ? `/diamonds/statement?limit=120&flow=${fluxo}`
          : `/ledger?limit=120&currency=${moeda}&flow=${fluxo}&kind=${tipo}&days=${dias}`;
      const { data } = await gbApi.get(url);
      setDados(data);
    } catch (e) { setDados({ entries: [] }); } finally { setLoad(false); }
  }, [scope, moeda, fluxo, tipo, dias]);

  useEffect(() => { buscar(); }, [buscar]);

  const entries = dados?.entries || [];
  const tipos = dados?.types || [];

  return (
    <div className="pb-10" data-testid={testid}>
      <h2 className="mb-3 flex items-center gap-2 text-base font-black tracking-wide text-orange-300 md:text-lg">
        <ScrollText className="h-4 w-4" />{title}
      </h2>

      <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid={`${testid}-filters`}>
        {scope === 'all' && (
          <>
            <Chip on={moeda === 'all'} onClick={() => setMoeda('all')} testid={`${testid}-cur-all`}>todas</Chip>
            <Chip on={moeda === 'coins'} onClick={() => setMoeda('coins')} testid={`${testid}-cur-coins`}>
              <GbCoin className="mr-1 h-[1em]" />amarelas
            </Chip>
            <Chip on={moeda === 'diamonds'} onClick={() => setMoeda('diamonds')} testid={`${testid}-cur-gems`}>
              <Gem className="mr-1 inline h-3 w-3" />diamantes
            </Chip>
            <span className="mx-1 h-4 w-px bg-purple-900/70" />
          </>
        )}
        <Chip on={fluxo === 'all'} onClick={() => setFluxo('all')} testid={`${testid}-flow-all`}>tudo</Chip>
        <Chip on={fluxo === 'in'} onClick={() => setFluxo('in')} testid={`${testid}-flow-in`}>
          <ArrowDownLeft className="mr-1 inline h-3 w-3" />entradas
        </Chip>
        <Chip on={fluxo === 'out'} onClick={() => setFluxo('out')} testid={`${testid}-flow-out`}>
          <ArrowUpRight className="mr-1 inline h-3 w-3" />saídas
        </Chip>
        {scope === 'all' && (
          <>
            <span className="mx-1 h-4 w-px bg-purple-900/70" />
            {PERIODOS.map((p) => (
              <Chip key={p.v} on={dias === p.v} onClick={() => setDias(p.v)} testid={`${testid}-days-${p.v}`}>{p.t}</Chip>
            ))}
            {tipos.length > 1 && (
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid={`${testid}-kind`}
                className="ml-1 rounded-full border border-purple-800/70 bg-black/50 px-3 py-1 text-[11px] font-bold text-purple-200 outline-none">
                <option value="all">todos os movimentos</option>
                {tipos.map((t) => <option key={t} value={t}>{LEDGER_LABEL[t] || t}</option>)}
              </select>
            )}
          </>
        )}
      </div>

      {scope !== 'all' && dados && (
        <div className="mb-3 grid gap-2 sm:grid-cols-3">
          <div className={`rounded-2xl border px-3 py-2 ${scope === 'coins'
            ? 'border-amber-700/50 bg-amber-950/20' : 'border-cyan-800/50 bg-cyan-950/20'}`}
            data-testid={`${testid}-balance`}>
            <p className={`text-[10px] font-black uppercase tracking-widest ${scope === 'coins' ? 'text-amber-400/70' : 'text-cyan-400/70'}`}>saldo na carteira</p>
            {scope === 'coins'
              ? <p className="font-mono text-lg font-black text-amber-200"><GbCoin className="mr-1" />{gbFmt(dados.coins_balance)}</p>
              : <p className="flex items-center gap-1 font-mono text-lg font-black text-cyan-200"><Gem className="h-4 w-4" />{gbFmt(dados.diamonds_balance)}</p>}
          </div>
          <div className="rounded-2xl border border-emerald-800/50 bg-emerald-950/15 px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400/70">entradas listadas</p>
            <p className="font-mono text-lg font-black text-emerald-200">+{gbFmt(dados.entradas)}</p>
          </div>
          <div className="rounded-2xl border border-rose-800/50 bg-rose-950/15 px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-rose-400/70">saídas listadas</p>
            <p className="font-mono text-lg font-black text-rose-200">−{gbFmt(dados.saidas)}</p>
          </div>
        </div>
      )}

      {load && <p className="flex items-center gap-2 text-xs text-purple-300/60"><Loader2 className="h-3.5 w-3.5 animate-spin" />carregando…</p>}
      {!load && entries.length === 0 && (
        <p className="text-xs text-purple-300/60" data-testid={`${testid}-empty`}>Nenhuma movimentação com esse filtro.</p>
      )}

      {!load && entries.length > 0 && (
        <div className="divide-y divide-purple-900/50 overflow-hidden rounded-2xl border border-purple-800/50 bg-black/30"
          data-testid={`${testid}-list`}>
          {entries.map((e, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5" data-testid={`${testid}-row-${i}`}>
              <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full ${e.amount >= 0 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
                {e.amount >= 0 ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-purple-100">{LEDGER_LABEL[e.entry_type] || e.entry_type}</p>
                <p className="text-[10px] text-purple-300/50">
                  {new Date(e.created_at).toLocaleString('pt-BR')}
                  {e.metadata?.node ? ` · fase ${e.metadata.node}` : ''}
                  {e.metadata?.method ? ` · ${e.metadata.method === 'pix' ? 'PIX' : 'MB WAY'}` : ''}
                </p>
              </div>
              <span className={`flex flex-none items-center gap-1 font-mono text-sm font-black ${e.amount >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                {e.amount >= 0 ? '+' : '−'}{gbFmt(Math.abs(e.amount))}
                {e.metadata?.currency === 'coins' ? <GbCoin /> : <Gem className="h-3.5 w-3.5 text-cyan-300" />}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
